import styled from '@emotion/styled'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import { Alert, Box, Button as MuiButton, CircularProgress } from '@mui/material'
import { StrictMode, useCallback, useEffect, useState } from 'react'
import { FormProvider, SubmitHandler, UseFormReturn, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useTypedDispatch, useTypedSelector } from '../..'
import { analyzeSuggestionsAction } from '../../actions/defectAssistant'
import {
  initializeCreateDefectAssistant,
  resetCreateDefectAssistant,
} from '../../reducers/createDefectAssistantSlice'
import { resetStatus } from '../../reducers/createDefectSlice'
import { getColor, theme } from '../../Theme'
import { ActionStatus } from '../../types/Input'
import DefectsList from '../defects/DefectsList'
import ErrorText from '../ErrorText'
import LoadingIndicator from '../LoadingIndicator'
import Text from '../Text'
import ConfirmationDialog from './ConfirmationDialog'
import { ActionButton, AiButton, EscapeLink } from './CtaButtons'
import { fieldPicker } from './FormFieldCreator'
import ManualFlow from './ManualFlow'
import PhoneSupportNotice from './PhoneSupportNotice'
import ScrollAiFlow from './ScrollAiFlow'
import { CreateDefectFormData, Equipment, FieldConfig, inputTypes } from './types'
import ValidationPanel from './ValidationPanel'

const MediumErrorText = styled(ErrorText)`
  ${theme.text('normal', 'decorative', 'bold')};
  margin: ${theme.spacing.sizes.large};
  color: red;
`

const SubHeaderText = styled(Text)`
  text-transform: uppercase;
  color: ${(p) => getColor(p.theme, ['grayDark'], ['grayPink'])};
`

const DualSelectionRowContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;
  margin-top: 20px;
`

const FullRowSelectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 20px;
`

const ButtonText = styled.span`
  flex-grow: 1;
`

const OpenDefectsContainer = styled.div`
  margin-top: 20px;
  margin-bottom: 5px;
`

const ValidationActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
  margin-bottom: 50px;
`

const MAX_AI_IMAGES = 3

interface CreateDefectComponentProps {
  onSubmit: (payload: CreateDefectFormData) => void
  createDefectStatus: ActionStatus
  fields: FieldConfig
  formMethods: UseFormReturn<CreateDefectFormData>
  failedToFetchOptions: Array<CallableFunction>
  selectedEquipment?: Equipment
  resetForm: (resetEquipment: boolean) => void
  scrollToTop: () => void
  openDefectsEnabled: boolean
  applyAiSuggestion: (defectCode: string, defectType: string) => void
}

type FlowMode = 'initial' | 'aiScroll' | 'manualNoAi' | 'manualValidated' | 'manualAfterAi'
type ManualFlowMode = Extract<FlowMode, 'manualNoAi' | 'manualValidated' | 'manualAfterAi'>

const isManualFlowMode = (flowMode: FlowMode): flowMode is ManualFlowMode =>
  flowMode === 'manualNoAi' || flowMode === 'manualValidated' || flowMode === 'manualAfterAi'

const CreateDefectComponent = ({
  onSubmit,
  createDefectStatus,
  fields,
  formMethods,
  failedToFetchOptions,
  selectedEquipment,
  resetForm,
  scrollToTop,
  openDefectsEnabled,
  applyAiSuggestion,
}: CreateDefectComponentProps): JSX.Element => {
  const { t } = useTranslation()
  const dispatch = useTypedDispatch()

  const response = useTypedSelector((state) => state.defect.response)
  const attachmentUploadFailures = useTypedSelector(
    (state) => state.defect.attachmentUploadFailures
  )

  const nightMode = useTypedSelector((state) => state.system.nightMode)
  const defects = useTypedSelector((state) => state.defects.openDefects)
  const fetchDefectsStatus = useTypedSelector((state) => state.defects.openStatus)
  const assetBytes = useTypedSelector((state) => state.defect.assetBytes)
  const flowStep = useTypedSelector((state) => state.createDefectAssistant.flow.step)

  const [id, setId] = useState(response?.id)
  const [showOpenDefects, setShowOpenDefects] = useState<boolean>(false)
  const [flowMode, setFlowMode] = useState<FlowMode>('initial')

  useEffect(() => {
    return () => {
      dispatch(resetStatus())
      dispatch(resetCreateDefectAssistant())
    }
  }, [dispatch])

  useEffect(() => {
    if (response?.id) {
      setId(response.id)
    }
  }, [response])

  const { control, getValues, handleSubmit, setValue, trigger } = formMethods
  const comment = useWatch({ control, name: 'comment' }) ?? ''
  const vehicleType = useWatch({ control, name: 'vehicleType' }) ?? ''
  const installationGroup = useWatch({ control, name: 'installationGroup' }) ?? ''

  const imagesToSend = Object.values(assetBytes)
    .filter((entry) => entry.mimeType.startsWith('image/'))
    .slice(0, MAX_AI_IMAGES)

  const onSubmitHandler: SubmitHandler<CreateDefectFormData> = (data: CreateDefectFormData) => {
    onSubmit({ ...data, criticality: '3', aiAssisted: flowMode === 'manualAfterAi' })
  }

  const resetCreateDefectStatus = useCallback((): void => {
    dispatch(resetStatus())
  }, [dispatch])

  const startAiAnalyzeFlow = async (): Promise<void> => {
    const equipmentSelected = await trigger('installationGroup')
    if (!equipmentSelected) {
      scrollToTop()
      return
    }

    const trimmedComment = comment.trim()
    setValue('aiComment', '')
    dispatch(
      initializeCreateDefectAssistant({
        comment: trimmedComment,
        images: imagesToSend,
        vehicleType,
        installationGroup,
      })
    )
    dispatch(analyzeSuggestionsAction())
  }

  const enterManualNoAi = useCallback((): void => {
    setFlowMode('manualNoAi')
  }, [])

  const enterManualAfterAi = useCallback((): void => {
    const aiComment = getValues('aiComment') ?? ''
    setValue('comment', aiComment)
    setFlowMode('manualAfterAi')
  }, [getValues, setValue])

  const handleValidationReadyChange = useCallback((ready: boolean): void => {
    setFlowMode(ready ? 'manualValidated' : 'manualNoAi')
  }, [])

  useEffect(() => {
    if (createDefectStatus === 'succeeded') {
      resetForm(true)
      dispatch(resetCreateDefectAssistant())
      setFlowMode('initial')
    }
  }, [createDefectStatus, resetForm, dispatch])

  useEffect(() => {
    if (flowStep !== 'idle' && flowStep !== 'analyzing' && flowMode === 'initial') {
      setFlowMode('aiScroll')
    }
  }, [flowStep, flowMode])

  return (
    <FormProvider {...formMethods}>
      {failedToFetchOptions.length > 0 && (
        <MediumErrorText>{t('errors.defect.optionsFailed')}</MediumErrorText>
      )}
      {flowMode !== 'aiScroll' && (
        <DualSelectionRowContainer>
          {fieldPicker({
            ...fields.installationGroup,
            title: t('defect.field.equipment'),
            key: 'installationGroup',
            type: inputTypes.auto,
            onChange: (value: string | null) => {
              if (fields.installationGroup?.onChange) {
                fields.installationGroup.onChange(value)
              }
              setShowOpenDefects(false)
              if (isManualFlowMode(flowMode)) {
                setFlowMode('manualNoAi')
              }
              if (flowStep === 'analyzing') {
                dispatch(resetCreateDefectAssistant())
              }
              setValue('aiComment', '')
            },
          })}
          {fieldPicker({
            ...fields.defectGroup,
            title: t('defect.field.defectGroup'),
            key: 'defectGroup',
            type: inputTypes.auto,
          })}
        </DualSelectionRowContainer>
      )}
      {openDefectsEnabled && (
        <OpenDefectsContainer>
          {!showOpenDefects ? (
            <AiButton onClick={() => setShowOpenDefects(defects.length > 0)}>
              <ButtonText>{t('defect.create.showOpen', { openCount: defects.length })}</ButtonText>
              {fetchDefectsStatus === 'loading' && (
                <Box paddingRight="5px">
                  <CircularProgress size={30} color="info" />
                </Box>
              )}
            </AiButton>
          ) : (
            <>
              {selectedEquipment && (
                <SubHeaderText>
                  {t('defect.list.header.open', {
                    installationGroup: selectedEquipment.installationGroup,
                    itemId: selectedEquipment.itemId,
                  })}
                </SubHeaderText>
              )}
              <DefectsList defects={defects} t={t} nightMode={nightMode} status="open" />
              <Box textAlign="center">
                <MuiButton
                  onClick={() => {
                    setShowOpenDefects(false)
                    scrollToTop()
                  }}
                  variant="outlined"
                  sx={{ color: 'info.main' }}
                  startIcon={<CloseOutlinedIcon />}
                >
                  {t('defect.closeOpenedDefects')}
                </MuiButton>
              </Box>
            </>
          )}
        </OpenDefectsContainer>
      )}

      <FullRowSelectionContainer>
        {flowMode === 'initial' && (
          <>
            {fieldPicker({
              ...fields.comment,
              title: t('defect.field.comment'),
              key: 'comment',
              type: inputTypes.input,
            })}
            {fieldPicker({
              title: t('defect.field.attachments'),
              key: 'digitalAssetUuids',
              type: inputTypes.upload,
              instructions: t('defect.attachmentInstructions'),
              onChange: fields.digitalAssetUuids.onChange,
              fileWord: t('defect.attachmentFiles'),
              required: fields.digitalAssetUuids.required,
              files: fields.digitalAssetUuids.files,
              status: fields.digitalAssetUuids.status,
            })}
          </>
        )}

        {flowMode === 'aiScroll' && (
          <StrictMode>
            <ScrollAiFlow
              applyAiSuggestion={applyAiSuggestion}
              onEnterManualNoAi={enterManualNoAi}
              onEnterManualAfterAi={enterManualAfterAi}
            />
          </StrictMode>
        )}

        {flowMode === 'initial' && (
          <>
            <PhoneSupportNotice />
            <AiButton
              disabled={
                (comment.trim().length === 0 && imagesToSend.length === 0) ||
                flowStep === 'analyzing'
              }
              onClick={() => void startAiAnalyzeFlow()}
            >
              {flowStep === 'analyzing' ? (
                <>
                  <CircularProgress size={20} sx={{ color: 'white' }} />
                  {t('defect.aiAssistant.analyzing')}
                </>
              ) : (
                t('defect.aiAssistant.analyze')
              )}
            </AiButton>
            <EscapeLink type="button" onClick={enterManualNoAi}>
              {t('defect.aiAssistant.continueToForm')}
            </EscapeLink>
          </>
        )}

        {isManualFlowMode(flowMode) && (
          <ManualFlow fields={fields} afterAi={flowMode === 'manualAfterAi'} />
        )}
      </FullRowSelectionContainer>
      {createDefectStatus === 'loading' && <LoadingIndicator size="normal" padded={true} />}
      {createDefectStatus === 'failed' && (
        <Alert variant="filled" severity="error">
          {t('errors.defect.failed')}
        </Alert>
      )}
      {createDefectStatus === 'succeeded' && (
        <ConfirmationDialog
          closeDialog={() => {
            resetCreateDefectStatus()
            window.history.back()
          }}
          submitHandler={() => {
            resetCreateDefectStatus()
            scrollToTop()
          }}
          dialogText={`${t('defect.create.succeeded', { id })}${
            attachmentUploadFailures.length
              ? ` ${t('errors.defect.attachmentUploadFailed', {
                  files: attachmentUploadFailures.join(', '),
                })}`
              : ''
          }`}
          primaryActionText={t('defect.createNew')}
          secondaryActionText={t('back')}
        />
      )}
      {isManualFlowMode(flowMode) && (
        <ValidationActionsContainer>
          {(flowMode === 'manualNoAi' || flowMode === 'manualValidated') && (
            <ValidationPanel
              onValidationReadyChange={handleValidationReadyChange}
              validationReady={flowMode === 'manualValidated'}
            />
          )}
          {(flowMode === 'manualValidated' || flowMode === 'manualAfterAi') && (
            <ActionButton onClick={handleSubmit(onSubmitHandler)}>{t('send')}</ActionButton>
          )}
        </ValidationActionsContainer>
      )}
    </FormProvider>
  )
}

export default CreateDefectComponent
